import React, { FC, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@ui/components/Button'
import { ContentBlock } from '@ui/components/ContentBlock/ContentBlock'
import { FeatureIcon } from '@ui/components/FeatureIcon/FeatureIcon'
import { Link } from '@ui/components/Link'
import { WarningIcon } from '@ui/icons/Warning.icon'

import { WarningPageStatus } from '@model/AccountDetailedDto'

import { useHomePageUrl } from '@hooks/useHomePageUrl'
import { safeLocalStorage } from '@hooks/useStorage'

import { Box } from '@shared/components/Box'
import { FullScreenFooter } from '@shared/templates/FullScreenLayout'

interface WarningAccountPageProps {
  status: WarningPageStatus
}

export const WarningAccountPage: FC<WarningAccountPageProps> = ({ status }) => {
  const { t } = useTranslation()

  const homePageUrl = useHomePageUrl()

  const isStillMounted = useRef(true)

  useEffect(() => {
    isStillMounted.current = false

    const timer = setTimeout(() => {
      isStillMounted.current = true
    }, 100)

    return () => {
      if (isStillMounted.current) {
        safeLocalStorage.setItem(`hasVisited${status}AccountPage`, 'true')
      }
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const Content: Record<WarningPageStatus, { title: string; body: string }> = {
    Closed: {
      title: t('Your account is currently closed'),
      body: t('To reactivate your account, please read and accept the terms'),
    },
    Inactive: {
      title: t('Your account is currently inactive'),
      body: `${t('Due to inactivity your account became inactive')} ${t(
        'To reactivate your account, please read and accept the terms'
      )}`,
    },
  }

  return (
    <Box flex direction='column' gap='x32' align='center' minHeight='100%'>
      <ContentBlock
        variant='voucher'
        icon={<FeatureIcon size='lg' icon={WarningIcon} bg='surface-warning-high-em' />}
        title={Content[status].title}
        description={Content[status].body}
      />
      <FullScreenFooter>
        <Link href={`${homePageUrl}/trading/demo-account`}>{t('Get Your Free Demo Account')}</Link>
        <Button.Primary size='lg' fullWidth>
          {t('Reactivate Account')}
        </Button.Primary>
      </FullScreenFooter>
    </Box>
  )
}
